import { useRef, useEffect, useState, useCallback } from 'react'

export const useAsyncEffect = (callback, deps = []) => {
  useEffect(() => {
    callback()
  }, deps)
}

const defaultPagingState = {
  items: [],
  count: 0,
  page: 1,
  pageSize: 20,
  loading: false,
  loaded: false,
  error: null,
}

export const usePatchableState = (initialState = {}) => {
  const [state, setState] = useState(initialState)

  const patchState = useCallback((data) => {
    setState((prevState) => {
      return Object.assign({}, prevState, typeof data === 'function' ? data(prevState) : data)
    })
  }, [])

  return [state, patchState]
}

const defaultParamsMapper = (data) => data
const defaultDataMapper = (data) => data

export const usePagingState = ({ dataLoader, paramsMapper = defaultParamsMapper, dataMapper = defaultDataMapper, manual = false, initialState }) => {
  const [pagingState, setPagingState] = usePatchableState(Object.assign({}, defaultPagingState, initialState))
  const { count, page, pageSize } = pagingState

  const loadDataForPage = useCallback(async (page, pageSize) => {
    try {
      setPagingState({ page, pageSize, loading: true })
      const { items, count } = await dataLoader(paramsMapper({ page, pageSize })).then(dataMapper)
      setPagingState({ items, count, loading: false, loaded: true, error: null })
    } catch (error) {
      setPagingState({ loading: false, error })
    }
  }, [])

  const reloadCurrentPage = useCallback(() => {
    setPagingState(({ page, pageSize, loading }) => {
      !loading && loadDataForPage(page, pageSize)
      return {}
    })
  }, [])

  const reloadToFirstPage = useCallback(() => {
    setPagingState(({ pageSize, loading }) => {
      !loading && loadDataForPage(1, pageSize)
      return {}
    })
  }, [])

  const switchPage = useCallback(
    (page) => {
      page <= Math.ceil(count / pageSize) && page >= 1 && loadDataForPage(page, pageSize)
    },
    [count, pageSize]
  )

  const switchPageSize = useCallback(
    (pageSize) => {
      pageSize = Math.ceil(pageSize)
      if (pageSize <= 0 || isNaN(pageSize)) throw new TypeError('无效的PageSize')
      loadDataForPage(Math.min(Math.ceil(count / pageSize), page), pageSize)
    },
    [count, page, switchPage]
  )

  const switchToNextPage = useCallback(() => {
    switchPage(page + 1)
  }, [switchPage, page])

  const switchToPrevPage = useCallback(() => {
    switchPage(page - 1)
  }, [switchPage, page])

  useEffect(() => {
    !manual && loadDataForPage(page, pageSize)
  }, [])

  return { pagingState, setPagingState, reloadCurrentPage, reloadToFirstPage, switchPageSize, switchPage, switchToNextPage, switchToPrevPage }
}

export const useCountdown = (seconds = 60, interval = 1000) => {
  const [current, setValue] = useState(seconds)
  const timer = useRef()
  const update = useCallback(() => {
    setValue((value) => {
      return value === 0 ? (clearTimeout(timer.current), 60) : Math.max(0, value - 1)
    })
    timer.current = setTimeout(update, interval)
  }, [])

  const start = useCallback(() => {
    update()
  }, [])

  const pause = useCallback(() => {
    clearTimeout(timer.current)
  }, [])

  const stop = useCallback(() => {
    setValue(seconds)
    learTimeout(timer.current)
  }, [])

  useEffect(() => {
    return () => {
      clearTimeout(timer.current)
    }
  }, [])

  return { current, start, pause, stop }
}

export const useRequest = (requester, initialValue, deps = []) => {
  const [value, setValue] = useState(initialValue)

  const loadData = useCallback(() => {
    requester(deps).then(setValue)
  }, [deps])

  useEffect(() => {
    loadData()
  }, deps)

  return [value, setValue, loadData]
}